<script setup>
import { ref, computed, watch } from 'vue'
import { CalendarDate, getLocalTimeZone } from '@internationalized/date'
import { CalendarIcon, X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RangeCalendar } from '@/components/ui/range-calendar'

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({ start: null, end: null }),
    },
    placeholder: {
        type: String,
        default: 'Pick a date range',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    numberOfMonths: {
        type: Number,
        default: 2,
    },
    minDate: {
        type: String,
        default: null,
    },
    maxDate: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['update:modelValue'])

// Convert ISO string to CalendarDate
const isoToCalendarDate = (isoString) => {
    if (!isoString) return undefined
    try {
        const date = new Date(isoString)
        return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
    } catch (e) {
        return undefined
    }
}

// Convert CalendarDate to ISO string (start of day)
const calendarDateToIso = (calendarDate) => {
    if (!calendarDate) return null
    const date = calendarDate.toDate(getLocalTimeZone())
    date.setHours(0, 0, 0, 0)
    return date.toISOString()
}

// Compare two CalendarDates for equality
const datesEqual = (a, b) => {
    if (!a && !b) return true
    if (!a || !b) return false
    return a.year === b.year && a.month === b.month && a.day === b.day
}

// Internal range state using CalendarDate
const dateRange = ref({
    start: isoToCalendarDate(props.modelValue?.start),
    end: isoToCalendarDate(props.modelValue?.end),
})

const isOpen = ref(false)

// Min/max date constraints
const minValue = computed(() => isoToCalendarDate(props.minDate))
const maxValue = computed(() => isoToCalendarDate(props.maxDate))

// Default placeholder to minValue so calendar opens at earliest available date
const placeholderValue = computed(() => minValue.value)

// Flag to prevent circular updates
let isUpdatingFromProps = false
let isUpdatingFromInternal = false

// Watch for external changes (from parent)
watch(() => props.modelValue, (newVal) => {
    if (isUpdatingFromInternal) return

    const newStart = isoToCalendarDate(newVal?.start)
    const newEnd = isoToCalendarDate(newVal?.end)

    // Only update if values actually changed
    if (!datesEqual(dateRange.value.start, newStart) || !datesEqual(dateRange.value.end, newEnd)) {
        isUpdatingFromProps = true
        dateRange.value = { start: newStart, end: newEnd }
        isUpdatingFromProps = false
    }
}, { deep: true })

// Handle calendar selection
const handleCalendarUpdate = (newVal) => {
    if (isUpdatingFromProps) return

    dateRange.value = newVal

    // Only emit when we have a complete range
    if (newVal.start && newVal.end) {
        isUpdatingFromInternal = true
        emit('update:modelValue', {
            start: calendarDateToIso(newVal.start),
            end: calendarDateToIso(newVal.end),
        })
        // Close popover after selection
        isOpen.value = false
        // Reset flag after a tick
        setTimeout(() => { isUpdatingFromInternal = false }, 0)
    }
}

const hasValue = computed(() => dateRange.value.start && dateRange.value.end)

const displayText = computed(() => {
    if (!hasValue.value) return props.placeholder

    const formatDate = (calendarDate) => {
        if (!calendarDate) return ''
        const date = calendarDate.toDate(getLocalTimeZone())
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date)
    }

    return `${formatDate(dateRange.value.start)} - ${formatDate(dateRange.value.end)}`
})

const clearSelection = () => {
    dateRange.value = { start: undefined, end: undefined }
    emit('update:modelValue', { start: null, end: null })
}
</script>

<template>
    <Popover v-model:open="isOpen">
        <PopoverTrigger as-child>
            <Button
                variant="outline"
                size="sm"
                class="h-8 shrink-0 gap-1.5 border-dashed justify-start text-left font-normal whitespace-nowrap"
                :class="{ 'text-muted-foreground': !hasValue }"
                :disabled="disabled"
            >
                <CalendarIcon class="h-4 w-4" />
                <span class="truncate">{{ displayText }}</span>
                <template v-if="hasValue">
                    <span
                        class="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-muted-foreground/20 transition-colors"
                        @click.stop="clearSelection"
                    >
                        <X class="h-3.5 w-3.5" />
                    </span>
                </template>
            </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="start">
            <RangeCalendar
                :model-value="dateRange"
                :placeholder="placeholderValue"
                :number-of-months="numberOfMonths"
                :min-value="minValue"
                :max-value="maxValue"
                initial-focus
                @update:model-value="handleCalendarUpdate"
            />
        </PopoverContent>
    </Popover>
</template>
